import { useContext, useRef, useState } from "react";

import {
  BulletinResource,
  extractImagesFromPdf,
  extractTextFromPdf,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";

import LoadingModal from "@/common/LoadingModal";
import TriUploadFile from "@/common/TriUploadFile";
import "@/components/alerts/alerts.scss";
import { SingleAlertContext } from "@/components/alerts/context";
import { updateToContextStructure } from "@/components/alerts/utils";
import { IEvidenceFile, IPdfFile } from "@/types";

interface UploadPdfProps {
  setIsUploadPdfModalOpen: (arg: boolean) => void;
  setEvidenceFilesLoading: (arg: boolean) => void;
}

const UploadPdf = ({
  setIsUploadPdfModalOpen,
  setEvidenceFilesLoading,
}: UploadPdfProps) => {
  const pdfInput = useRef<HTMLInputElement>(null);
  const [isPdfUploading, setIsPDFUploading] = useState(false);
  const { setAlertProperties, setAlertProperty } =
    useContext(SingleAlertContext);

  const handleFileUploadPdf = (_event, files: FileList | null) => {
    if (!files || files.length === 0) {
      toast.error("No file selected.");
      return;
    }

    const file = files[0];
    const ext = file.name.match(/\.([^.]+)$/)?.[1];
    if (ext !== "pdf") {
      toast.error("File type not allowed");
      return;
    }
    setIsPDFUploading(true);
    extractTextFromPdf({ body: { file } })
      .then((res) => {
        const bulletin = res.data as BulletinResource;
        console.info("UPLOADED PDF", bulletin.pdf);
        setAlertProperty("pdf", bulletin.pdf as unknown as IPdfFile);
        const alertProperties = updateToContextStructure(bulletin);
        setAlertProperties(alertProperties);

        setIsPDFUploading(false);
        setIsUploadPdfModalOpen(false);
        setEvidenceFilesLoading(true);
        extractImagesFromPdf({
          path: { bulletin_id: bulletin.id },
          body: { file },
        }).then((imagesRes) => {
          const imagesBulletin = imagesRes.data as BulletinResource;
          setAlertProperty(
            "evidence_files",
            imagesBulletin.evidence_files as { [key: string]: IEvidenceFile },
          );
          setEvidenceFilesLoading(false);
        });
      })
      .catch((err) => {
        console.error(err);
        toast.error("Error uploading the PDF.");
        setIsPDFUploading(false);
      });
  };
  const loadingMessages = [
    "Extracting text from your PDF...",
    "Analyzing bulletin details...",
    "Creating a detailed description...",
    "Determining the precise location...",
    "Finding relevant contacts...",
    "Filling in bulletin details...",
    "Finishing up, almost there...",
  ];

  return (
    <div
      onClick={() => {
        if (pdfInput.current) {
          pdfInput.current.click();
        }
      }}
      style={{
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        borderRadius: "5px",
        padding: "10px",
      }}
    >
      {!isPdfUploading && (
        <TriUploadFile
          uploadMessage="UPLOAD AN EXISTING BULLETIN PDF"
          subMessage="We'll scan the PDF and pull its details into the bulletin form for you to review."
          fileInput={pdfInput}
          handleFileUpload={handleFileUploadPdf}
          accept="application/pdf"
          multiple={false}
        />
      )}
      {isPdfUploading && (
        <LoadingModal
          stages={loadingMessages}
          timingMessage="This may take a few seconds . . ."
        />
      )}
    </div>
  );
};

export default UploadPdf;
